import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import DownloadIcon from '@mui/icons-material/Download'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store'
import { annexesApi } from '@/store/api/purchaseContractApi'
import { baseApi } from '@/store/api/baseApi'
import { API_BASE_URL } from '@/config/api'
import dayjs from 'dayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import {
  useGetPurchaseRepInfoQuery,
  useSetPurchaseRepInfoMutation,
} from '@/store/api/purchaseContractApi'
import { usePreference } from '@/hooks/usePreference'

/**
 * Page-level filter shape — kept in sync with backend `PurchasePageFilter`
 * (app/services/preferences/baselines/purchase_page_filter.py). The default
 * row (username='default') is seeded by SQL migration 0011, so a freshly
 * loaded user always sees populated values without an extra PATCH round-trip.
 */
interface PurchasePageFilter {
  rep_info: number | null
  isForcedClose: 'all' | 'yes' | 'no'
  deliveryLatestFrom: string | null
  deliveryLatestTo: string | null
}

// Synced Purchase pages — render inside tabs (đồng bộ pattern /sales/contract/<tab>)
import LocalPListPage from '@/pages/purchase/LocalPListPage'
import NPEContractListPage from '@/pages/purchase/NPEContractListPage'
import ForwardListPage from '@/pages/purchase/ForwardListPage'
import AnnexListPage from '@/pages/purchase/AnnexListPage'
import PTBFFixationListPage from '@/pages/purchase/PTBFFixationListPage'
import PTBFRollingListPage from '@/pages/purchase/PTBFRollingListPage'
import NPEFixationListPage from '@/pages/purchase/NPEFixationListPage'
import NPEAllocationListPage from '@/pages/purchase/NPEAllocationListPage'
import LocalPPlansPage from '@/pages/purchase/LocalPPlansPage'
import PurchaseReferencesView from '@/pages/purchase/PurchaseReferencesView'
import PurchaseChooseRepInfoDialog from '@/pages/purchase/PurchaseChooseRepInfoDialog'
import PageHeader from '@/components/PageHeader'
import ReloadButton from '@/components/ReloadButton'
import {
  CARD_CONTENT_COMPACT,
  FILTER_HEADER_INPUT_SX,
  FILTER_HEADER_SELECT_SX,
  HIDE_ON_TABLET_INLINE,
} from '@/theme/responsive'

type ForcedCloseFilter = 'all' | 'yes' | 'no'

type PurchaseTabKey =
  | 'localP'
  | 'forwardP'
  | 'npeContract'
  | 'amendment'
  | 'ptbfFixation'
  | 'npeFixation'
  | 'npeAllocation'
  | 'ptbfRolling'
  | 'references'
  | 'plans'

const TAB_CONFIG: { key: PurchaseTabKey; label: string; segment: string }[] = [
  { key: 'localP', label: 'Purchase', segment: 'list' },
  { key: 'forwardP', label: 'Forward P', segment: 'forward-pu' },
  { key: 'npeContract', label: 'NPE Contract', segment: 'npe-contract' },
  { key: 'amendment', label: 'Amendment', segment: 'amendment' },
  { key: 'ptbfFixation', label: 'PTBF Fixation', segment: 'ptbf-fixation' },
  { key: 'npeFixation', label: 'NPE Fixation', segment: 'npe-fixation' },
  { key: 'npeAllocation', label: 'NPE Allocation', segment: 'npe-allocation' },
  { key: 'ptbfRolling', label: 'PTBF Rolling', segment: 'ptbf-rolling' },
  { key: 'plans', label: 'Plans', segment: 'plans' },
  { key: 'references', label: 'References', segment: 'references' },
]

function isPurchaseTabKey(value: string | null): value is PurchaseTabKey {
  return TAB_CONFIG.some((tab) => tab.key === value)
}

const headerButtonSx = {
  minHeight: 24,
  px: 0.75,
  py: 0.15,
  fontSize: '0.67rem',
  lineHeight: 1.1,
  textTransform: 'none' as const,
}

const headerLabelSx = {
  whiteSpace: 'nowrap',
  fontSize: '0.72rem',
  lineHeight: 1.1,
}

// Filter chung header sx — codify trong `@/theme/responsive` Pattern 11.
const compactInputSx = FILTER_HEADER_INPUT_SX
const compactSelectSx = FILTER_HEADER_SELECT_SX

const getFiscalYearDefaultRange = () => {
  const now = dayjs()
  const startYear = now.month() >= 9 ? now.year() : now.year() - 1

  return {
    startDate: `${startYear}-10-01`,
    endDate: `${startYear + 1}-09-30`,
  }
}

const getDownloadFilename = (disposition: string | null): string | null => {
  if (!disposition) return null

  const filenameStar = /filename\*\s*=\s*([^;]+)/i.exec(disposition)
  if (filenameStar?.[1]) {
    const value = filenameStar[1].trim().replace(/^UTF-8''/i, '').replace(/^"|"$/g, '')
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  const filename = /filename\s*=\s*([^;]+)/i.exec(disposition)
  if (!filename?.[1]) return null
  return filename[1].trim().replace(/^"|"$/g, '')
}

export default function PurchaseContractPage() {
  const location = useLocation()
  const navigate = useNavigate()

  // Single source of truth = URL. Đồng bộ pattern Sales Contract: KHÔNG dùng
  // useState cho activeTab vì gây dual-source-of-truth → có frame trung gian
  // render UI tab cũ trong lúc URL đã đổi nhưng state chưa kịp sync.
  const activeTab = useMemo<PurchaseTabKey>(() => {
    const routeSegment = location.pathname.split('/').filter(Boolean)[2]
    const fromRoute = TAB_CONFIG.find((tab) => tab.segment === routeSegment)?.key
    if (fromRoute) return fromRoute
    const queryTab = new URLSearchParams(location.search).get('tab')
    if (isPurchaseTabKey(queryTab)) return queryTab
    return 'localP'
  }, [location.pathname, location.search])
  // Per-tab filter config:
  //   label: nhãn DatePicker (Delivery Latest / Delivery Time (NPE) / hỗn hợp / disabled)
  //   datePickerEnabled: DatePicker có cho phép chỉnh không
  //   forcedCloseEnabled: select Is Forced Close có cho phép chỉnh không
  // BE đọc deliveryLatestFrom/To + isForcedClose từ user_preferences (giống SaleContract);
  // mỗi list service tự quyết apply lên cột nào (local_p.latest_delivery hoặc
  // consignment_p.delivery_time) hoặc bỏ qua (References).
  type TabFilterMode = {
    label: string
    datePickerEnabled: boolean
    forcedCloseEnabled: boolean
  }
  const TAB_FILTER: Record<PurchaseTabKey, TabFilterMode> = {
    localP:        { label: 'Delivery Latest',                 datePickerEnabled: true,  forcedCloseEnabled: true  },
    forwardP:      { label: 'Delivery Latest',                 datePickerEnabled: true,  forcedCloseEnabled: true  },
    npeContract:   { label: 'Delivery Time (NPE)',             datePickerEnabled: true,  forcedCloseEnabled: false },
    amendment:     { label: 'Delivery Latest (P)/Time (NPE)',  datePickerEnabled: true,  forcedCloseEnabled: true  },
    ptbfFixation:  { label: 'Delivery Latest',                 datePickerEnabled: true,  forcedCloseEnabled: true  },
    npeFixation:   { label: 'Delivery Time (NPE)',             datePickerEnabled: true,  forcedCloseEnabled: false },
    npeAllocation: { label: 'Delivery Time (NPE)',             datePickerEnabled: true,  forcedCloseEnabled: false },
    ptbfRolling:   { label: 'Delivery Latest',                 datePickerEnabled: true,  forcedCloseEnabled: true  },
    references:    { label: 'Delivery Latest',                 datePickerEnabled: false, forcedCloseEnabled: false },
    plans:         { label: 'Delivery Latest (P)/Time (NPE)',  datePickerEnabled: true,  forcedCloseEnabled: true  },
  }
  const tabFilter = TAB_FILTER[activeTab]
  const [deliveryLatestFrom, setDeliveryLatestFrom] = useState('')
  const [deliveryLatestTo, setDeliveryLatestTo] = useState('')
  const [isForcedClose, setIsForcedClose] = useState<ForcedCloseFilter>('all')
  const [qualityPriceOpen, setQualityPriceOpen] = useState(false)
  const [exporting, setExporting] = useState<'all' | 'filter' | null>(null)

  // Gate render tab con: chỉ mount sau khi filter prefs hydrate xong → tránh
  // list API bị gọi 2 lần (lần 1 filter rỗng, lần 2 đúng).
  const [filtersReady, setFiltersReady] = useState(false)
  const prefsHydrated = useRef(false)
  const lastPatchedRef = useRef<{ deliveryLatestFrom: string; deliveryLatestTo: string; isForcedClose: ForcedCloseFilter } | null>(null)
  const dispatch = useAppDispatch()
  const pageFilterPref = usePreference<PurchasePageFilter>('purchase', 'page_filter')
  const { data: repInfo } = useGetPurchaseRepInfoQuery()
  const [setPurchaseRepInfo] = useSetPurchaseRepInfoMutation()
  const email = useAppSelector((s) => s.auth.user?.email ?? '')
  const exportToken = 'JDHK-NDEB-DBCK-DGEJ'

  // Endpoint names theo tab đang active — nút Reload sẽ "busy" khi 1 trong các query
  // này đang `pending` trong cache (Redux state.api.queries / state.tlcerpApi.queries).
  const watchedEndpoints = useMemo<string[]>(() => {
    switch (activeTab) {
      case 'localP':
        return ['listLocalP']
      case 'forwardP':
        return ['listLocalPVer2']
      case 'npeContract':
        return ['listNPEContracts']
      case 'amendment':
        return ['listAnnexes']
      case 'ptbfFixation':
        return ['listPTBFFixations']
      case 'npeFixation':
        return ['listNPEFixations']
      case 'npeAllocation':
        return ['listNPEAllocations']
      case 'ptbfRolling':
        return ['listPTBFRollings']
      case 'plans':
        return ['listPlans']
      case 'references':
        return ['getPurchaseReferenceRows']
      default:
        return []
    }
  }, [activeTab])

  const downloadPurchaseContractExcel = async (mode: 'all' | 'filter', url: string) => {
    if (!email) return

    setExporting(mode)
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const filename = getDownloadFilename(res.headers.get('content-disposition'))
      const downloadName =
        mode === 'filter' && filename
          ? filename.replace(/\.xlsx$/i, '_filter.xlsx')
          : filename
      a.href = href
      a.download = downloadName ?? (mode === 'filter' ? 'PurchaseContract_filter.xlsx' : 'PurchaseContract.xlsx')
      a.click()
      URL.revokeObjectURL(href)
    } finally {
      setExporting(null)
    }
  }

  const handleExportAll = async () => {
    const url = `${API_BASE_URL}/erp-legacy/download/purchasecontract/data?token=${encodeURIComponent(exportToken)}&email=${encodeURIComponent(email)}`
    await downloadPurchaseContractExcel('all', url)
  }

  const handleExportFilter = async () => {
    const url = `${API_BASE_URL}/erp-legacy/download/purchasecontract/data_filter?token=${encodeURIComponent(exportToken)}&email=${encodeURIComponent(email)}&start_date=${encodeURIComponent(deliveryLatestFrom)}&end_date=${encodeURIComponent(deliveryLatestTo)}&is_forced_close=${encodeURIComponent(isForcedClose)}`
    await downloadPurchaseContractExcel('filter', url)
  }

  useEffect(() => {
    if (pageFilterPref.isLoading || prefsHydrated.current) return
    prefsHydrated.current = true

    const { value } = pageFilterPref
    const hydratedFrom = value.deliveryLatestFrom ?? ''
    const hydratedTo = value.deliveryLatestTo ?? ''
    const hydratedForced: ForcedCloseFilter = (value.isForcedClose ?? 'all') as ForcedCloseFilter

    lastPatchedRef.current = {
      deliveryLatestFrom: hydratedFrom,
      deliveryLatestTo: hydratedTo,
      isForcedClose: hydratedForced,
    }

    setDeliveryLatestFrom(hydratedFrom)
    setDeliveryLatestTo(hydratedTo)
    setIsForcedClose(hydratedForced)
    setFiltersReady(true)
  }, [pageFilterPref])

  useEffect(() => {
    if (repInfo?.currentSignId == null) {
      void setPurchaseRepInfo(1)
    }
  }, [repInfo?.currentSignId, setPurchaseRepInfo])

  useEffect(() => {
    if (!prefsHydrated.current) return

    const last = lastPatchedRef.current
    if (last) {
      const unchanged =
        last.deliveryLatestFrom === deliveryLatestFrom &&
        last.deliveryLatestTo === deliveryLatestTo &&
        last.isForcedClose === isForcedClose
      if (unchanged) return
    }

    const timer = window.setTimeout(() => {
      lastPatchedRef.current = {
        deliveryLatestFrom,
        deliveryLatestTo,
        isForcedClose,
      }
      ;(async () => {
        try {
          await pageFilterPref.patch({
            deliveryLatestFrom: deliveryLatestFrom || null,
            deliveryLatestTo: deliveryLatestTo || null,
            isForcedClose,
          })
        } catch {
          // ignore PATCH failure — UX vẫn cho phép tiếp tục
        }
        // BE đọc filter từ user_preferences ⇒ invalidate tag data của mọi tab
        // để các list query refetch lại với filter mới (parity SaleContract flow).
        dispatch(
          baseApi.util.invalidateTags([
            { type: 'LocalP', id: 'LIST' },
            { type: 'LocalPVer2', id: 'LIST' },
            { type: 'LocalPForward', id: 'LIST' },
            { type: 'LocalPPlan', id: 'LIST' },
            { type: 'NPEContract', id: 'LIST' },
            { type: 'NPEFixation', id: 'LIST' },
            { type: 'NPEAlloc', id: 'LIST' },
            { type: 'PTBFFixation', id: 'LIST' },
            { type: 'PTBFRolling', id: 'LIST' },
            { type: 'ContractAll', id: 'LIST' },
          ]),
        )
        dispatch(annexesApi.util.invalidateTags([{ type: 'Annex', id: 'LIST' }]))
      })()
    }, 450)
    return () => window.clearTimeout(timer)
    // pageFilterPref.patch là useCallback stable → KHÔNG đưa object pageFilterPref
    // vào deps (object mới mỗi render → loop PATCH /user-preferences).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryLatestFrom, deliveryLatestTo, isForcedClose, dispatch])

  // Redirect về /purchase/contract/list nếu route gốc (chưa có tab segment).
  useEffect(() => {
    if (!location.pathname.startsWith('/purchase/contract')) return
    const routeSegment = location.pathname.split('/').filter(Boolean)[2]
    const hasRouteTab = TAB_CONFIG.some((tab) => tab.segment === routeSegment)
    if (!hasRouteTab) {
      navigate('/purchase/contract/list', { replace: true })
    }
  }, [location.pathname, navigate])

  const hasInvalidRange = deliveryLatestFrom !== '' && deliveryLatestTo !== '' && deliveryLatestFrom > deliveryLatestTo

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Purchase Contract"
        module="Purchase"
        actions={
        <Stack
          direction="row"
          spacing={0.45}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
          sx={{ width: '100%' }}
        >
          {/* Date RANGE filter (Pattern 11): DatePicker đầu có label `tabFilter.label`,
              icon `→` ở giữa, DatePicker thứ 2 KHÔNG label. Tab-aware label
              (delivery period / forward period theo tab). */}
          <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={tabFilter.label}
                format="DD-MMM-YYYY"
                disabled={!tabFilter.datePickerEnabled}
                value={deliveryLatestFrom ? dayjs(deliveryLatestFrom) : null}
                onChange={(d) => setDeliveryLatestFrom(d ? d.format('YYYY-MM-DD') : getFiscalYearDefaultRange().startDate)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: compactInputSx,
                    InputLabelProps: { shrink: true },
                    inputProps: { 'aria-label': `${tabFilter.label} start date` },
                  },
                }}
              />
              <ArrowRightAltIcon
                fontSize="small"
                sx={{
                  color: tabFilter.datePickerEnabled ? 'text.secondary' : 'text.disabled',
                  fontSize: { xs: 16, sm: 18 },
                  mx: 0.25,
                }}
                aria-hidden="true"
              />
              <DatePicker
                format="DD-MMM-YYYY"
                disabled={!tabFilter.datePickerEnabled}
                value={deliveryLatestTo ? dayjs(deliveryLatestTo) : null}
                onChange={(d) => setDeliveryLatestTo(d ? d.format('YYYY-MM-DD') : getFiscalYearDefaultRange().endDate)}
                slotProps={{
                  textField: {
                    size: 'small',
                    error: tabFilter.datePickerEnabled && hasInvalidRange,
                    sx: compactInputSx,
                    inputProps: { 'aria-label': `${tabFilter.label} end date` },
                  },
                }}
              />
            </LocalizationProvider>
          </Stack>

          {/* Is Forced Close — Pattern 11: label inside (shrink) */}
          <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap">
            <TextField
              label="Is Forced Close"
              select
              size="small"
              value={isForcedClose}
              onChange={(event) => setIsForcedClose(event.target.value as ForcedCloseFilter)}
              disabled={!tabFilter.forcedCloseEnabled}
              InputLabelProps={{ shrink: true }}
              sx={compactSelectSx}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Stack>

          <Button
            size="small"
            variant="outlined"
            sx={headerButtonSx}
            onClick={() => setQualityPriceOpen(true)}
            title="Choose Rep Info"
          >
            {/* sm: "Rep Info" — md+: "Choose Rep Info" */}
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Choose{' '}</Box>
            Rep Info
          </Button>
          <ReloadButton
            sx={headerButtonSx}
            apiTags={['PurchaseContractPrefs', 'PurchaseReference']}
            baseTags={['LocalP', 'LocalPVer2', 'LocalPPlan', 'Annex', 'PTBFFixation', 'PTBFRolling', 'NPEContract', 'NPEFixation', 'NPEAlloc', 'ContractAll']}
            watchEndpoints={watchedEndpoints}
          />
          <Button
            size="small"
            variant="outlined"
            sx={headerButtonSx}
            onClick={handleExportFilter}
            disabled={exporting !== null || !email || hasInvalidRange}
            startIcon={
              exporting === 'filter' ? (
                <CircularProgress size={11} color="inherit" />
              ) : (
                <DownloadIcon fontSize="small" />
              )
            }
            title="Export (Filter)"
          >
            {/* Pattern 10: discriminator "Filter" luôn show; md+ wrap "Export ( )" */}
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Export ({' '}</Box>
            Filter
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>)</Box>
          </Button>
          <Button
            size="small"
            variant="outlined"
            sx={headerButtonSx}
            onClick={handleExportAll}
            disabled={exporting !== null || !email}
            startIcon={
              exporting === 'all' ? (
                <CircularProgress size={11} color="inherit" />
              ) : (
                <DownloadIcon fontSize="small" />
              )
            }
            title="Export (All)"
          >
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Export ({' '}</Box>
            All
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>)</Box>
          </Button>
        </Stack>
        }
      />
      {hasInvalidRange ? (
        <Typography variant="caption" color="error" sx={{ alignSelf: 'flex-end', mb: 1 }}>
          Start date must be before or equal to end date
        </Typography>
      ) : null}
      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeTab}
          className="pc-purchase-tabs"
          onChange={(_, value) => {
            const tab = TAB_CONFIG.find((t) => t.key === value)
            if (tab) navigate(`/purchase/contract/${tab.segment}`, { replace: false })
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 34,
            '& .MuiTabs-indicator': {
              height: 2.5,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              position: 'relative',
              minHeight: 34,
              minWidth: 0,
              py: 0.25,
              px: 1.5,
              fontSize: '0.78rem',
              fontWeight: 600,
              letterSpacing: 0.15,
              textTransform: 'none',
              color: 'text.secondary',
              borderRadius: '6px 6px 0 0',
              transition: 'color 140ms ease, background-color 140ms ease',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              },
              '&:not(:last-of-type)::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 16,
                width: '1px',
                bgcolor: 'divider',
                opacity: 0.6,
                transition: 'opacity 140ms ease',
              },
              '&:hover::after, &.Mui-selected::after': { opacity: 0 },
            },
          }}
        >
          {TAB_CONFIG.map((tab) => (
            <Tab key={tab.key} label={tab.label} value={tab.key} />
          ))}
        </Tabs>
        <CardContent sx={CARD_CONTENT_COMPACT}>
          {!filtersReady ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
              <CircularProgress size={28} />
            </Box>
          ) : activeTab === 'localP' ? (
            <LocalPListPage
              deliveryLatestFrom={deliveryLatestFrom}
              deliveryLatestTo={deliveryLatestTo}
              isForcedClose={isForcedClose}
            />
          ) : activeTab === 'forwardP' ? (
            <ForwardListPage
              deliveryLatestFrom={deliveryLatestFrom}
              deliveryLatestTo={deliveryLatestTo}
              isForcedClose={isForcedClose}
            />
          ) : activeTab === 'npeContract' ? (
            <NPEContractListPage />
          ) : activeTab === 'amendment' ? (
            <AnnexListPage />
          ) : activeTab === 'ptbfFixation' ? (
            <PTBFFixationListPage />
          ) : activeTab === 'npeFixation' ? (
            <NPEFixationListPage />
          ) : activeTab === 'npeAllocation' ? (
            <NPEAllocationListPage />
          ) : activeTab === 'ptbfRolling' ? (
            <PTBFRollingListPage />
          ) : activeTab === 'references' ? (
            <PurchaseReferencesView />
          ) : activeTab === 'plans' ? (
            <LocalPPlansPage />
          ) : null}
        </CardContent>
      </Card>

      <PurchaseChooseRepInfoDialog open={qualityPriceOpen} onClose={() => setQualityPriceOpen(false)} />
    </Box>
  )
}
